import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import ChartEmptyState from './ChartEmptyState';

export default function DashboardLineChart({
  data = [],
  height = 280,
  series = [],
  xKey = 'time_bucket',
  emptyState = null,
  yAxisFormatter = (v) => v,
  tooltipFormatter = (v) => v,
  dualAxis = false,
}) {
  const chartData = useMemo(() => (data?.length ? data : []), [data]);
  const hasData = chartData.some((row) =>
    series.some((s) => row[s.key] != null && row[s.key] !== 0));

  const renderedSeries = useMemo(
    () => series.filter((s) => chartData.some((row) => row[s.key] != null)),
    [chartData, series],
  );

  if (!hasData) {
    return emptyState || <ChartEmptyState />;
  }

  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '12px',
        boxShadow: 'var(--shadow-lg)',
      }}
      >
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
          {label}
        </p>
        {payload.map((entry) => (
          <p key={entry.dataKey} style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: entry.color }}>
            {`${entry.name}: ${tooltipFormatter(entry.value, entry.dataKey)}`}
          </p>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
          tickLine={false}
          axisLine={{ stroke: 'var(--color-border)' }}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={yAxisFormatter}
        />
        {dualAxis && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
          />
        )}
        <Tooltip content={customTooltip} />
        {renderedSeries.length > 1 && <Legend />}
        {renderedSeries.map((s) => (
          <Line
            key={s.key}
            yAxisId={dualAxis && s.yAxisId ? s.yAxisId : 'left'}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
